/**
 * Service for employee-related API calls.
 * Matches the legacy AngularJS employeeService endpoints.
 */

import { EmployeeInfo, EmployeeProfile, SupervisorProfile } from '../types/employee';
import { getEmployeeApiUrl, getPickerApiUrl } from '../utils/api';
import { apiFetch } from './apiClient';

/**
 * GET /{warehouseId}/Employee/GetCurrentUser
 * Returns the authenticated user's login from Midway auth context.
 * On the AFTX domain (*.aft.amazonoperations.app), falls back to reading
 * the login directly from the fcmenu-nonsensitive cookie, since the API
 * Gateway authorizer doesn't set principalId for FCMenu-authenticated requests.
 */
export async function getCurrentUser(): Promise<string> {
    // On AFTX domain, try reading login from the fcmenu-nonsensitive cookie first
    if (typeof window !== 'undefined' && window.location.host.endsWith('.aft.amazonoperations.app')) {
        const cookieLogin = getLoginFromFcMenuCookie();
        if (cookieLogin) {
            return cookieLogin;
        }
    }

    const url = getEmployeeApiUrl('GetCurrentUser');
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to get current user: ${response.status} - ${text}`);
    }

    const data = await response.json();
    return data.userId || '';
}

/**
 * Reads the login from fcmenu-employeeLogin or aftxmenu-nonsensitive cookie.
 * FCMenu sets individual cookies: fcmenu-employeeLogin=<username>
 */
function getLoginFromFcMenuCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const trimmed = cookie.trim();
        // Direct login cookie (most common)
        if (trimmed.startsWith('fcmenu-employeeLogin=')) {
            const login = decodeURIComponent(trimmed.substring('fcmenu-employeeLogin='.length));
            if (login && login !== 'null' && login !== 'undefined') {
                return login;
            }
        }
        // Fallback: query-string style nonsensitive cookie
        if (trimmed.startsWith('fcmenu-nonsensitive=') ||
            trimmed.startsWith('aftxmenu-nonsensitive=') ||
            trimmed.startsWith('aftx-nonsensitive=')) {
            const value = trimmed.substring(trimmed.indexOf('=') + 1);
            const params = value.split('&');
            for (const param of params) {
                if (param.startsWith('login=')) {
                    const login = decodeURIComponent(param.substring('login='.length));
                    if (login && login !== 'null' && login !== 'undefined') {
                        return login;
                    }
                }
            }
        }
    }
    return null;
}

/**
 * GET /{warehouseId}/Employee/IsValid/{userId}
 * Checks whether a given user ID is a valid employee in the warehouse.
 */
export async function isEmployeeValid(userId: string): Promise<boolean> {
    const url = getEmployeeApiUrl(`IsValid/${encodeURIComponent(userId)}`);
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to validate employee: ${response.status} - ${text}`);
    }

    const data = await response.json();
    return data.isValid || false;
}

/**
 * GET /{warehouseId}/Employee/GetEmployeeInfo/{id}
 * Returns employee info for a given user ID or badge barcode ID.
 */
export async function getEmployeeInfo(id: string): Promise<EmployeeInfo> {
    const url = getEmployeeApiUrl(`GetEmployeeInfo/${encodeURIComponent(id)}`);
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to get employee info: ${response.status} - ${text}`);
    }

    const data = await response.json();
    return data.employeeInfo;
}

/**
 * GET /{warehouseId}/Employee/GetName/{userId}
 * Returns just the display name for a given user ID.
 */
export async function getEmployeeName(userId: string): Promise<string> {
    const url = getEmployeeApiUrl(`GetName/${encodeURIComponent(userId)}`);
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to get employee name: ${response.status} - ${text}`);
    }

    const data = await response.json();
    return data.name || '';
}

/**
 * GET /{warehouseId}/Employee/GetEmployeeProfilesByWarehouseId
 * Returns all active employee profiles for the warehouse.
 */
export async function getEmployeeProfilesByWarehouseId(): Promise<EmployeeProfile[]> {
    const url = getEmployeeApiUrl('GetEmployeeProfilesByWarehouseId');
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to get employee profiles: ${response.status} - ${text}`);
    }

    const data = await response.json();
    return data.employeeProfiles || [];
}

/**
 * GET /{warehouseId}/Employee/GetSupervisorProfilesByWarehouseId
 * Returns supervisor profiles with their subordinate employee IDs.
 */
export async function getSupervisorProfilesByWarehouseId(): Promise<SupervisorProfile[]> {
    const url = getEmployeeApiUrl('GetSupervisorProfilesByWarehouseId');
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to get supervisor profiles: ${response.status} - ${text}`);
    }

    const data = await response.json();
    return data.supervisorProfiles || [];
}

/**
 * GET /{warehouseId}/Picker/GetAllPickersInfoByWarehouseId
 * Returns active pickers from FCEligibilityService (same source as the legacy website).
 * This uses GetAllActivePickers which gets picker IDs from Dispatcher/Workstream,
 * then enriches with AEPS profiles — matching the old website's "Select Pickers" behavior.
 */
export async function getActivePickerProfiles(): Promise<EmployeeProfile[]> {
    const url = getPickerApiUrl('GetAllPickersInfoByWarehouseId');
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to get active pickers: ${response.status} - ${text}`);
    }

    const data = await response.json();
    // The API returns 'pickers' (from Smithy PickerInfoList)
    const pickers = data.pickers || data.pickerProfiles || [];
    return pickers.map((p: any) => ({
        userId: p.userId || p.login || '',
        employeeId: p.employeeId || '',
        name: p.name || '',
        supervisorName: p.supervisorName || '',
        supervisorEmployeeId: p.supervisorEmployeeId || '',
        badgeBarcodeId: p.badgeBarcodeId || '',
        activelyEmployed: p.activelyEmployed !== false && p.isActive !== false,
    }));
}
